import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Stack, Typography, FormControl, InputLabel, Select, MenuItem,
  TextField, Alert, Box, Link as MuiLink, alpha, useTheme
} from '@mui/material';
import { PersonAddAlt1, EventNote } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { format } from 'date-fns';
import { Session, SessionClass, SectionInstance } from '../../types/sessions';
import { sessionsApi } from '../../services/sessions/sessionsApi';
import { classesApi } from '../../services/sessions/classesApi';
import { sectionInstancesApi } from '../../services/sessions/sectionInstancesApi';
import { enrollmentsApi } from '../../services/sessions/enrollmentsApi';

interface EnrollExistingStudentDialogProps {
  open: boolean;
  studentId: string;
  studentName?: string;
  onClose: () => void;
  onEnrolled: () => void;
}

/**
 * "Enroll an already-known student into a new session" — the path for
 * re-admission after a gap, pre-enrolling in a planning session, or
 * mid-year intake of a returning student. See plan §5.3.
 */
const EnrollExistingStudentDialog: React.FC<EnrollExistingStudentDialogProps> = ({
  open, studentId, studentName, onClose, onEnrolled
}) => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();

  const [sessions, setSessions] = useState<Session[]>([]);
  const [classes, setClasses]   = useState<SessionClass[]>([]);
  const [sections, setSections] = useState<SectionInstance[]>([]);

  const [sessionId, setSessionId] = useState<string>('');
  const [classId, setClassId]     = useState<string>('');
  const [sectionId, setSectionId] = useState<string>('');
  const [rollNumber, setRollNumber] = useState<string>('');
  const [showRollOverride, setShowRollOverride] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSessionId(''); setClassId(''); setSectionId(''); setRollNumber('');
    let cancelled = false;
    (async () => {
      try {
        const [allSessions, allClasses] = await Promise.all([
          sessionsApi.list(),
          classesApi.list()
        ]);
        if (cancelled) return;
        // Only active/planning sessions accept enrollments.
        setSessions(allSessions.filter(s => s.status === 'active' || s.status === 'planning'));
        setClasses(allClasses);
      } catch (err: any) {
        enqueueSnackbar(err?.message || 'Could not load options', { variant: 'error' });
      }
    })();
    return () => { cancelled = true; };
  }, [open, enqueueSnackbar]);

  useEffect(() => {
    if (!sessionId || !classId) { setSections([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const secs = await sectionInstancesApi.list({ sessionId, classId });
        if (!cancelled) setSections(secs);
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [sessionId, classId]);

  const submit = async () => {
    if (!sessionId || !classId) return;
    if (rollNumber && (!Number.isInteger(Number(rollNumber)) || Number(rollNumber) < 1)) {
      enqueueSnackbar('Roll number must be a positive integer', { variant: 'warning' });
      return;
    }
    setSubmitting(true);
    try {
      await enrollmentsApi.createForExistingStudent({
        studentId,
        sessionId,
        classId,
        sectionInstanceId: sectionId || null,
        rollNumber: rollNumber ? Number(rollNumber) : undefined
      });
      enqueueSnackbar('Student enrolled into the new session', { variant: 'success' });
      onEnrolled();
      onClose();
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Could not enroll', { variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const selectedSession = sessions.find(s => s._id === sessionId);

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle component="div" sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1.25}>
          <PersonAddAlt1 color="primary" fontSize="small" />
          <Typography variant="h6" fontWeight={600}>Enroll in another session</Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Re-admission after a gap, or pre-enrolling <strong>{studentName ?? 'this student'}</strong> in a
          session that's about to start.
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {sessions.length === 0 ? (
          <Alert severity="warning" variant="outlined">
            No active or planning sessions available. Create one from Academic Sessions first.
          </Alert>
        ) : (
          <Stack spacing={2}>
            <FormControl fullWidth>
              <InputLabel>Session</InputLabel>
              <Select value={sessionId} onChange={(e) => { setSessionId(e.target.value); setClassId(''); setSectionId(''); }} label="Session">
                {sessions.map(s => (
                  <MenuItem key={s._id} value={s._id}>
                    {s.name}
                    <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      · {format(new Date(s.startDate), 'MMM yyyy')}–{format(new Date(s.endDate), 'MMM yyyy')} · {s.status}
                    </Typography>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <FormControl fullWidth disabled={!sessionId}>
                <InputLabel>Class</InputLabel>
                <Select value={classId} onChange={(e) => { setClassId(e.target.value); setSectionId(''); }} label="Class">
                  {classes.map(c => <MenuItem key={c._id} value={c._id}>{c.name}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl fullWidth disabled={!classId || sections.length === 0}>
                <InputLabel>Section</InputLabel>
                <Select value={sectionId} onChange={(e) => setSectionId(e.target.value)} label="Section">
                  <MenuItem value=""><em>{sections.length ? 'No section' : 'No sections in this class'}</em></MenuItem>
                  {sections.map(s => <MenuItem key={s._id} value={s._id}>{s.name}</MenuItem>)}
                </Select>
              </FormControl>
            </Stack>

            {/* Roll number — auto-assigned by default. When override is on,
                a full-width TextField matches the visual weight of the Class
                and Section fields above. */}
            {showRollOverride ? (
              <TextField
                label="Roll number"
                placeholder="e.g. 12"
                value={rollNumber}
                onChange={(e) => setRollNumber(e.target.value)}
                helperText={
                  <Box component="span">
                    Positive integer — must be unique in this class/section.{' '}
                    <MuiLink
                      component="button"
                      type="button"
                      variant="caption"
                      onClick={(e) => { e.preventDefault(); setShowRollOverride(false); setRollNumber(''); }}
                      sx={{ textDecorationStyle: 'dotted' }}
                    >
                      Use auto-assignment instead
                    </MuiLink>
                  </Box>
                }
                fullWidth
                autoFocus
              />
            ) : (
              <Typography variant="caption" color="text.secondary">
                Roll number will be auto-assigned.{' '}
                <MuiLink
                  component="button"
                  type="button"
                  variant="caption"
                  onClick={() => setShowRollOverride(true)}
                  sx={{ textDecorationStyle: 'dotted' }}
                >
                  Set manually instead
                </MuiLink>
              </Typography>
            )}

            {selectedSession?.status === 'planning' && (
              <Box sx={{
                px: 1.5, py: 1, borderRadius: 1.5,
                bgcolor: alpha(theme.palette.info.main, 0.07),
                border: `1px solid ${alpha(theme.palette.info.main, 0.25)}`
              }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <EventNote sx={{ fontSize: 16, color: 'info.dark' }} />
                  <Typography variant="caption" color="info.dark">
                    This is a planning session — students enroll now, attend once you activate it.
                  </Typography>
                </Stack>
              </Box>
            )}
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button
          variant="contained"
          disableElevation
          disabled={!sessionId || !classId || submitting}
          onClick={submit}
        >
          {submitting ? 'Enrolling…' : 'Enroll'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EnrollExistingStudentDialog;
